import { DomainTypeEnum } from '@pypestream/api-services';
import { StepperWC } from '@pypestream/design-system';
import { RefObject, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  Environments,
  FormProjectEnvironmentsType,
  FormProjectInfoType,
  FormProjectLocalizationType,
  FormProjectStateType,
  FormProjectToolingType,
  OutputProjectFormType,
} from '../..';
import {
  sendManagerEvent,
  useManagerStateMatches,
} from '../../../xstate/app.xstate';

const formProjectInfoDefaultValues = {
  name: '',
  description: '',
  projectIconId: '',
  projectIcon: '',
};

const formProjectLocalizationDefaultValues = {
  timeZoneId: '',
};

const formProjectToolingDefaultValues = {
  productIds: [],
};

const formProjectEnvironmentsDefaultValues = {
  projectEnvironmentConfigs: [
    {
      name: Environments.Production,
      domains: [{ type: DomainTypeEnum.Domain, url: '' }],
    },
    {
      name: Environments.Testing,
      domains: [],
    },
    {
      name: Environments.Development,
      domains: [],
    },
  ],
};

const outputProjectEnvironmentsDefaultValues = {
  projectEnvironmentConfigs: [],
  applets: [],
};

const stateDefaultValues: FormProjectStateType = {
  ...formProjectInfoDefaultValues,
  ...formProjectLocalizationDefaultValues,
  ...formProjectToolingDefaultValues,
  ...outputProjectEnvironmentsDefaultValues,
};

export const useProjectWizardForm = (swiperRef: RefObject<StepperWC>) => {
  const [projectFormState, setProjectFormState] =
    useState<OutputProjectFormType>(stateDefaultValues);

  const isProjectAdded = useManagerStateMatches(
    'orgRelated.ready.projectDetails.reloadAfterDelay'
  );

  const projectInfoForm = useForm<FormProjectInfoType>({
    defaultValues: formProjectInfoDefaultValues,
  });

  const projectEnvironmentsForm = useForm<FormProjectEnvironmentsType>({
    defaultValues: formProjectEnvironmentsDefaultValues,
    // https://react-hook-form.com/docs/useform#mode
    // This option allows you to configure the validation strategy before a user submits the form.
    // all: Validation is triggered on both blur and change events.
    mode: 'all',
  });

  const projectToolingForm = useForm<FormProjectToolingType>({
    defaultValues: formProjectToolingDefaultValues,
  });

  const projectLocalizationForm = useForm<FormProjectLocalizationType>({
    defaultValues: formProjectLocalizationDefaultValues,
  });

  const updateFormState = (formData?: Partial<OutputProjectFormType>) => {
    if (!formData) {
      return;
    }

    setProjectFormState((prevState) => ({
      ...prevState,
      ...formData,
    }));
  };

  const createProject = ({
    formData,
    callback,
  }: {
    callback: () => void;
    formData?: Partial<OutputProjectFormType>;
  }) => {
    const projectState = {
      ...projectFormState,
      ...formData,
    };

    sendManagerEvent({
      type: 'manager.addProject',
      name: projectState.name,
      description: projectState.description,
      productIds: projectState.productIds,
      timeZoneId: projectState.timeZoneId,
      projectEnvironmentConfigs: projectState.projectEnvironmentConfigs,
      projectIconId: projectState.projectIconId,
      callback: (res) => {
        if (!res) return;
        callback();
      },
    });
  };

  const resetFormState = () => {
    projectInfoForm.reset();
    projectEnvironmentsForm.reset();
    projectToolingForm.reset();
    projectLocalizationForm.reset();
    setProjectFormState(stateDefaultValues);
    swiperRef?.current?.goToStep(0);
  };

  const handleNextStep = (formData?: Partial<OutputProjectFormType>) => {
    updateFormState(formData);
    swiperRef?.current?.nextStep();
  };

  const handlePrevStep = () => swiperRef?.current?.backStep();

  useEffect(() => {
    if (isProjectAdded) {
      swiperRef?.current?.nextStep();
    }
  }, [isProjectAdded, swiperRef]);

  return {
    updateFormState,
    resetFormState,
    createProject,
    projectInfoForm,
    projectEnvironmentsForm,
    projectToolingForm,
    projectLocalizationForm,
    handleNextStep,
    handlePrevStep,
  };
};
